import type { CloudSetupState, FabricaAccountState } from "./CloudSetupPanel.logic";

export type CloudOnboardingStepId = "account" | "runtime" | "authorize";

/**
 * `blocked` is a dead end this client cannot clear — a build without Clerk or
 * a relay URL. It reads differently from `upcoming`, which only waits its turn.
 */
export type CloudOnboardingStepStatus = "done" | "current" | "upcoming" | "blocked";

export interface CloudOnboardingStep {
  readonly id: CloudOnboardingStepId;
  readonly title: string;
  readonly description: string;
  readonly status: CloudOnboardingStepStatus;
}

export interface CloudOnboardingInput {
  readonly accountState: FabricaAccountState;
  readonly relayReady: boolean;
  readonly cloudSetupState: CloudSetupState;
}

export type CloudPanelBody = "loading" | "onboarding" | "runtime";

/**
 * The panel guides setup until this client knows about a Cloud environment,
 * and reports the runtime afterwards. Basing that on the environment catalog —
 * rather than on the lifecycle state — keeps a merely stopped or disconnected
 * runtime from replaying first-run setup. While the catalog is still
 * hydrating, neither body is truthful yet, so the header stands alone.
 */
export function resolveCloudPanelBody(input: {
  readonly cloudEnvironmentCount: number;
  readonly selectedEnvironmentIsCloud: boolean;
  readonly cloudSetupState: CloudSetupState;
}): CloudPanelBody {
  if (input.cloudSetupState === "loading") return "loading";
  if (input.selectedEnvironmentIsCloud) return "runtime";
  return input.cloudEnvironmentCount === 0 ? "onboarding" : "runtime";
}

function accountStep(input: CloudOnboardingInput): CloudOnboardingStep {
  const base = { id: "account", title: "Sign in to Fabrica" } as const;
  switch (input.accountState) {
    case "signed-in":
      return {
        ...base,
        description: "Signed in. New Cloud environments will be connected to this account.",
        status: "done",
      };
    case "loading":
      return { ...base, description: "Checking your Fabrica session.", status: "current" };
    case "signed-out":
      return {
        ...base,
        description:
          "Sign in once before creating your first Cloud environment. Setup continues on its own afterwards.",
        status: "current",
      };
    case "unconfigured":
      return {
        ...base,
        description: "Fabrica account sign-in is not configured for this build yet.",
        status: "blocked",
      };
  }
}

function runtimeStep(input: CloudOnboardingInput): CloudOnboardingStep {
  const base = { id: "runtime", title: "Start the AgentCore runtime" } as const;
  if (input.accountState !== "signed-in") {
    return {
      ...base,
      description: "Starts an isolated AgentCore runtime for this Fabrica account.",
      status: "upcoming",
    };
  }
  if (!input.relayReady) {
    return {
      ...base,
      description:
        "Cloud compute will stay off until the relay URL and account token template are configured.",
      status: "blocked",
    };
  }
  switch (input.cloudSetupState) {
    case "authorize":
    case "starting":
    case "ready":
      return { ...base, description: "The AgentCore runtime is running.", status: "done" };
    case "error":
      return {
        ...base,
        description:
          "Fabrica Cloud cannot reach its managed runtime yet. Retry, or contact Fabrica support if the problem continues.",
        status: "current",
      };
    case "loading":
      return {
        ...base,
        description: "Reading connected execution environments.",
        status: "upcoming",
      };
    default:
      return {
        ...base,
        description:
          "Starts an isolated AgentCore runtime and connects it to this Fabrica account. Local execution remains the default and free target.",
        status: "current",
      };
  }
}

function authorizeStep(input: CloudOnboardingInput): CloudOnboardingStep {
  const base = { id: "authorize", title: "Authorize Fabrica Connect" } as const;
  switch (input.cloudSetupState) {
    case "authorize":
      return {
        ...base,
        description:
          "Authorize in the browser, then paste the one-time Connect code below. This is needed once so the Cloud environment can appear on your devices.",
        status: "current",
      };
    case "starting":
    case "ready":
      return {
        ...base,
        description: "Authorization succeeded.",
        status: "done",
      };
    default:
      return {
        ...base,
        description: "Links the runtime to your account so it appears on your devices.",
        status: "upcoming",
      };
  }
}

export function presentCloudOnboarding(
  input: CloudOnboardingInput,
): ReadonlyArray<CloudOnboardingStep> {
  return [accountStep(input), runtimeStep(input), authorizeStep(input)];
}

/**
 * Clerk reports sign-in through its own session state rather than through a
 * completion callback, so a setup click made while signed out is remembered
 * and resumed here. Without it the first click only opens the sign-in modal
 * and the user has to press the same button a second time.
 */
export function resolveCloudOnboardingResume(input: {
  readonly pending: boolean;
  readonly accountState: FabricaAccountState;
  readonly relayReady: boolean;
}): "run" | "hold" | "cancel" {
  if (!input.pending) return "hold";
  switch (input.accountState) {
    case "signed-in":
      return input.relayReady ? "run" : "cancel";
    case "unconfigured":
      return "cancel";
    case "loading":
    case "signed-out":
      return "hold";
  }
}
